import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import PersonIcon from '../icons/PersonIcon';
import OverlayButtonStack from './OverlayButtonStack';

interface CameraButtonsRootProps {
  children?: ReactNode;
  variant?: 'simple' | 'full';
  tooltipSide?: 'left' | 'right';
  inModal?: boolean;
  className?: string;
}

interface CameraButtonsContextValue {
  variant: 'simple' | 'full';
}

const CameraButtonsContext = createContext<CameraButtonsContextValue>({
  variant: 'simple',
});

const Root = ({
  children,
  variant = 'simple',
  tooltipSide = 'right',
  inModal = false,
  className,
}: CameraButtonsRootProps) => {
  return (
    <CameraButtonsContext.Provider value={{ variant }}>
      <OverlayButtonStack.Root
        variant={variant}
        tooltipSide={tooltipSide}
        inModal={inModal}
        title={variant === 'full' ? 'Camera controls' : undefined}
        titleClassName="w-[96px]"
        className={className}
      >
        {children}
      </OverlayButtonStack.Root>
    </CameraButtonsContext.Provider>
  );
};

const CameraFollow = ({
  cameraFollow,
  setCameraFollow,
  disabled,
}: {
  cameraFollow: boolean;
  setCameraFollow: (cameraFollow: boolean) => void;
  disabled?: boolean;
}) => {
  const { variant } = useContext(CameraButtonsContext);

  return (
    <OverlayButtonStack.Button
      tooltipText="Follow"
      text={variant === 'full' ? 'Lock' : undefined}
      active={cameraFollow}
      icon={cameraFollow ? <LockIcon /> : <LockOpenIcon />}
      onClick={() => setCameraFollow(!cameraFollow)}
      disabled={disabled}
    />
  );
};

const InputMode = ({
  inputMode,
  setCameraInputMode,
  disabled,
}: {
  inputMode: 'rotate' | 'truck' | 'dolly';
  setCameraInputMode: (inputMode: 'rotate' | 'truck' | 'dolly') => void;
  disabled?: boolean;
}) => {
  const { variant } = useContext(CameraButtonsContext);

  return (
    <>
      <OverlayButtonStack.Button
        tooltipText="Rotate"
        text={variant === 'full' ? 'Rotate' : undefined}
        active={inputMode === 'rotate'}
        icon={<RotateIcon />}
        onClick={() => setCameraInputMode('rotate')}
        disabled={disabled}
      />
      <OverlayButtonStack.Button
        tooltipText="Pan"
        text={variant === 'full' ? 'Pan' : undefined}
        active={inputMode === 'truck'}
        icon={<MoveIcon />}
        onClick={() => setCameraInputMode('truck')}
        disabled={disabled}
      />
    </>
  );
};

const LockRootMotion = ({
  enabled,
  toggleRootMotion,
  disabled,
}: {
  enabled: boolean;
  toggleRootMotion: () => void;
  disabled?: boolean;
}) => {
  const { variant } = useContext(CameraButtonsContext);

  return (
    <OverlayButtonStack.Button
      tooltipText="Lock Character Motion"
      text={variant === 'full' ? 'Lock Character Motion' : undefined}
      active={enabled}
      icon={<PersonIcon />}
      onClick={toggleRootMotion}
      disabled={disabled}
    />
  );
};

const Zoom = ({
  zoomIn,
  zoomOut,
  disabled,
}: {
  zoomIn: () => void;
  zoomOut: () => void;
  disabled?: boolean;
}) => {
  const { variant } = useContext(CameraButtonsContext);
  const [mouseDown, setMouseDown] = useState<'in' | 'out' | ''>('');
  const interval = useRef<number | null>(null);

  useEffect(() => {
    if (interval.current) {
      window.clearInterval(interval.current);
    }

    if (mouseDown === 'in') {
      interval.current = window.setInterval(zoomIn, 150);
    } else if (mouseDown === 'out') {
      interval.current = window.setInterval(zoomOut, 150);
    }

    return () => {
      if (interval.current) {
        window.clearInterval(interval.current);
      }
    };
  }, [mouseDown, zoomIn, zoomOut]);

  return (
    <>
      <OverlayButtonStack.Button
        tooltipText="Zoom in"
        text={variant === 'full' ? 'Zoom in' : undefined}
        className="-mb-1"
        icon={<PlusIcon />}
        onMouseDown={() => {
          zoomIn();
          setMouseDown('in');
        }}
        onMouseUp={() => setMouseDown('')}
        onMouseLeave={() => setMouseDown('')}
        disabled={disabled}
      />
      <OverlayButtonStack.Button
        tooltipText="Zoom out"
        text={variant === 'full' ? 'Zoom out' : undefined}
        icon={<MinusIcon />}
        onMouseDown={() => {
          zoomOut();
          setMouseDown('out');
        }}
        onMouseUp={() => setMouseDown('')}
        onMouseLeave={() => setMouseDown('')}
        disabled={disabled}
      />
    </>
  );
};

const Reset = ({
  resetCamera,
  disableCameraReset,
}: {
  resetCamera: () => void;
  disableCameraReset: boolean;
}) => {
  const { variant } = useContext(CameraButtonsContext);

  return (
    <OverlayButtonStack.Button
      tooltipText="Center camera"
      text={variant === 'full' ? 'Reset' : undefined}
      icon={<ResetIcon />}
      onClick={resetCamera}
      disabled={disableCameraReset}
    />
  );
};

const Center = ({
  centerCamera,
  disabled,
  tooltipText = 'Center on character',
  text,
}: {
  centerCamera: () => void;
  disabled?: boolean;
  tooltipText?: string;
  text?: string;
}) => {
  const { variant } = useContext(CameraButtonsContext);

  return (
    <OverlayButtonStack.Button
      tooltipText={tooltipText}
      text={text ?? (variant === 'full' ? 'Center' : undefined)}
      icon={<CenterIcon />}
      onClick={centerCamera}
      disabled={disabled}
    />
  );
};

export default {
  Root,
  CameraFollow,
  InputMode,
  Zoom,
  Reset,
  Center,
  LockRootMotion,
};

const RotateIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M7.32764 10.6074C7.32764 9.78646 7.40592 8.97819 7.5625 8.18262C7.72331 7.38704 7.96029 6.64225 8.27344 5.94824C8.59082 5.25 8.98014 4.64062 9.44141 4.12012L10.4126 4.65332C10.0021 5.12305 9.65723 5.67741 9.37793 6.31641C9.09863 6.9554 8.88704 7.64095 8.74316 8.37305C8.60352 9.10514 8.53369 9.84993 8.53369 10.6074C8.53369 11.3311 8.60352 12.0399 8.74316 12.7339C8.88704 13.4237 9.09863 14.0648 9.37793 14.6572C9.66146 15.2497 10.0106 15.7575 10.4253 16.1807L9.4541 16.7202C8.98014 16.2378 8.58447 15.6707 8.26709 15.019C7.95394 14.3674 7.71908 13.6649 7.5625 12.9116C7.40592 12.1584 7.32764 11.3903 7.32764 10.6074ZM9.78418 12.791C8.99707 12.791 8.22689 12.7127 7.47363 12.5562C6.72038 12.3953 6.0179 12.1584 5.36621 11.8452C4.71452 11.5321 4.15169 11.1385 3.67773 10.6646L4.21094 9.68701C4.63411 10.106 5.14193 10.4572 5.73438 10.7407C6.32682 11.02 6.96794 11.2316 7.65771 11.3755C8.35173 11.5151 9.06055 11.585 9.78418 11.585C10.5417 11.585 11.2865 11.513 12.0186 11.3691C12.7507 11.2253 13.4362 11.0137 14.0752 10.7344C14.7142 10.4551 15.2707 10.1102 15.7446 9.69971L16.2715 10.6709C15.751 11.1364 15.1416 11.5278 14.4434 11.8452C13.7493 12.1584 13.0046 12.3953 12.209 12.5562C11.4134 12.7127 10.6051 12.791 9.78418 12.791ZM9.99365 16.9741C9.09652 16.9741 8.25228 16.8027 7.46094 16.46C6.6696 16.1214 5.97135 15.6496 5.36621 15.0444C4.7653 14.4393 4.29346 13.741 3.95068 12.9497C3.60791 12.1584 3.43652 11.3141 3.43652 10.417C3.43652 9.51986 3.60791 8.67562 3.95068 7.88428C4.29346 7.09294 4.7653 6.39681 5.36621 5.7959C5.97135 5.19076 6.66748 4.7168 7.45459 4.37402C8.24593 4.03125 9.09017 3.85986 9.9873 3.85986C10.8887 3.85986 11.735 4.03125 12.5264 4.37402C13.3177 4.7168 14.016 5.19076 14.6211 5.7959C15.2262 6.39681 15.7002 7.09294 16.043 7.88428C16.3857 8.67562 16.5571 9.51986 16.5571 10.417C16.5571 11.3141 16.3857 12.1584 16.043 12.9497C15.7002 13.741 15.2262 14.4393 14.6211 15.0444C14.016 15.6496 13.3177 16.1214 12.5264 16.46C11.735 16.8027 10.8908 16.9741 9.99365 16.9741ZM9.99365 15.6792C10.7257 15.6792 11.4092 15.5438 12.0439 15.2729C12.6829 15.0021 13.2415 14.6255 13.7197 14.1431C14.2021 13.6606 14.5788 13.1021 14.8496 12.4673C15.1204 11.8325 15.2559 11.1491 15.2559 10.417C15.2559 9.68913 15.1204 9.00781 14.8496 8.37305C14.5788 7.73405 14.2 7.17334 13.7134 6.69092C13.231 6.2085 12.6724 5.83187 12.0376 5.56104C11.4028 5.2902 10.7194 5.15479 9.9873 5.15479C9.25944 5.15479 8.57601 5.2902 7.93701 5.56104C7.30225 5.83187 6.74577 6.2085 6.26758 6.69092C5.78939 7.17334 5.41488 7.73405 5.14404 8.37305C4.87321 9.00781 4.73779 9.68913 4.73779 10.417C4.73779 11.1491 4.87321 11.8325 5.14404 12.4673C5.41488 13.1021 5.78939 13.6606 6.26758 14.1431C6.75 14.6255 7.30859 15.0021 7.94336 15.2729C8.58236 15.5438 9.26579 15.6792 9.99365 15.6792Z" />
    </svg>
  );
};

const MoveIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path d="M2.89062 10.8677C2.75098 10.7323 2.68327 10.5778 2.6875 10.4043C2.69173 10.2308 2.76156 10.0785 2.89697 9.94727L4.92188 8.03027C5.06576 7.89486 5.21387 7.8335 5.36621 7.84619C5.51855 7.85889 5.64762 7.92448 5.75342 8.04297C5.85921 8.16146 5.91211 8.31169 5.91211 8.49365V9.8457H9.43506V6.30371H8.08301C7.90104 6.30371 7.75081 6.25081 7.63232 6.14502C7.51383 6.03923 7.44824 5.91016 7.43555 5.75781C7.42285 5.60547 7.48421 5.45947 7.61963 5.31982L9.53662 3.29492C9.67204 3.15527 9.82438 3.08333 9.99365 3.0791C10.1672 3.07487 10.3216 3.14469 10.457 3.28857L12.3804 5.31982C12.5158 5.45947 12.5771 5.60547 12.5645 5.75781C12.556 5.91016 12.4904 6.03923 12.3677 6.14502C12.2492 6.25081 12.099 6.30371 11.917 6.30371H10.5713V9.8457H14.0879V8.49365C14.0879 8.31169 14.1408 8.16146 14.2466 8.04297C14.3524 7.92448 14.4814 7.85889 14.6338 7.84619C14.7861 7.8335 14.9342 7.89486 15.0781 8.03027L17.1094 9.95361C17.249 10.089 17.3167 10.2435 17.3125 10.417C17.3083 10.5905 17.2384 10.7428 17.103 10.874L15.0781 12.791C14.9342 12.9264 14.7861 12.9878 14.6338 12.9751C14.4814 12.9624 14.3524 12.8968 14.2466 12.7783C14.1408 12.6598 14.0879 12.5096 14.0879 12.3276V10.9819H10.5713V14.5176H11.917C12.099 14.5176 12.2492 14.5705 12.3677 14.6763C12.4904 14.7778 12.556 14.9048 12.5645 15.0571C12.5771 15.2137 12.5158 15.3618 12.3804 15.5015L10.4634 17.5264C10.3322 17.666 10.1799 17.738 10.0063 17.7422C9.83285 17.7464 9.67839 17.6766 9.54297 17.5327L7.61963 15.5015C7.48421 15.3618 7.42285 15.2137 7.43555 15.0571C7.44824 14.9048 7.51383 14.7778 7.63232 14.6763C7.75081 14.5705 7.90104 14.5176 8.08301 14.5176H9.43506V10.9819H5.91211V12.3276C5.91211 12.5096 5.85921 12.6598 5.75342 12.7783C5.64762 12.8968 5.51855 12.9624 5.36621 12.9751C5.21387 12.9878 5.06576 12.9264 4.92188 12.791L2.89062 10.8677Z" />
    </svg>
  );
};

const PlusIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path d="M4.68701 10.417C4.68701 10.2223 4.75684 10.0552 4.89648 9.91553C5.03613 9.77165 5.20329 9.69971 5.39795 9.69971H9.28906V5.81494C9.28906 5.62028 9.35889 5.45312 9.49854 5.31348C9.63818 5.17383 9.80534 5.104 10 5.104C10.1989 5.104 10.3682 5.17383 10.5078 5.31348C10.6475 5.45312 10.7173 5.62028 10.7173 5.81494V9.69971H14.6021C14.7967 9.69971 14.9639 9.77165 15.1035 9.91553C15.2432 10.0552 15.313 10.2223 15.313 10.417C15.313 10.6159 15.2432 10.7852 15.1035 10.9248C14.9639 11.0602 14.7967 11.1279 14.6021 11.1279H10.7173V15.0254C10.7173 15.2158 10.6475 15.3809 10.5078 15.5205C10.3682 15.6602 10.1989 15.73 10 15.73C9.80534 15.73 9.63818 15.6602 9.49854 15.5205C9.35889 15.3809 9.28906 15.2158 9.28906 15.0254V11.1279H5.39795C5.20329 11.1279 5.03613 11.0602 4.89648 10.9248C4.75684 10.7852 4.68701 10.6159 4.68701 10.417Z" />
    </svg>
  );
};

const MinusIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path d="M5.3916 11.1406C5.26888 11.1406 5.15462 11.1089 5.04883 11.0454C4.94303 10.9777 4.85628 10.891 4.78857 10.7852C4.72087 10.6751 4.68701 10.5545 4.68701 10.4233C4.68701 10.2964 4.72087 10.1779 4.78857 10.0679C4.85628 9.95785 4.94303 9.87109 5.04883 9.80762C5.15462 9.74414 5.26888 9.7124 5.3916 9.7124H14.6021C14.7248 9.7124 14.839 9.74414 14.9448 9.80762C15.0549 9.87109 15.1416 9.95785 15.2051 10.0679C15.2728 10.1779 15.3066 10.2964 15.3066 10.4233C15.3066 10.5545 15.2728 10.6751 15.2051 10.7852C15.1416 10.891 15.0549 10.9777 14.9448 11.0454C14.839 11.1089 14.7248 11.1406 14.6021 11.1406H5.3916Z" />
    </svg>
  );
};

const ResetIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path d="M2.24316 10.4297L3.76025 8.29688C3.87874 8.13607 4.01416 8.05778 4.1665 8.06201C4.32308 8.06201 4.4585 8.1403 4.57275 8.29688L6.0835 10.436C6.22738 10.6265 6.26758 10.7979 6.2041 10.9502C6.14062 11.1025 5.98828 11.1787 5.74707 11.1787H4.79492C4.87956 11.8135 5.0721 12.4038 5.37256 12.9497C5.67725 13.4956 6.06022 13.9738 6.52148 14.3843C6.98698 14.7948 7.51595 15.1121 8.1084 15.3364C8.70085 15.5649 9.33138 15.6792 10 15.6792C10.7617 15.6792 11.4727 15.529 12.1328 15.2285C12.7972 14.9281 13.3643 14.5197 13.834 14.0034C13.9863 13.8553 14.1471 13.7791 14.3164 13.7749C14.4899 13.7664 14.6401 13.8215 14.7671 13.9399C14.894 14.0542 14.9575 14.2023 14.9575 14.3843C14.9618 14.562 14.8877 14.7271 14.7354 14.8794C14.1514 15.5184 13.4489 16.0262 12.6279 16.4028C11.8112 16.7837 10.9352 16.9741 10 16.9741C9.15788 16.9741 8.36442 16.826 7.61963 16.5298C6.87484 16.2336 6.20833 15.8188 5.62012 15.2856C5.0319 14.7567 4.5516 14.141 4.1792 13.4385C3.8068 12.736 3.57406 11.9827 3.48096 11.1787H2.57959C2.33415 11.1787 2.1818 11.1025 2.12256 10.9502C2.06331 10.7979 2.10352 10.6243 2.24316 10.4297ZM5.26465 5.95459C5.84863 5.31982 6.54899 4.81201 7.36572 4.43115C8.18669 4.05029 9.06478 3.85986 10 3.85986C10.8337 3.85986 11.6229 4.01009 12.3677 4.31055C13.1167 4.60677 13.7853 5.02148 14.3735 5.55469C14.9618 6.08366 15.4421 6.69938 15.8145 7.40186C16.1868 8.10433 16.4217 8.85758 16.519 9.66162H17.4014C17.6468 9.66162 17.7992 9.73779 17.8584 9.89014C17.9219 10.0425 17.8838 10.216 17.7441 10.4106L16.2334 12.5435C16.1149 12.7043 15.9774 12.7847 15.8208 12.7847C15.6685 12.7804 15.5352 12.7 15.4209 12.5435L13.9038 10.4106C13.7642 10.216 13.724 10.0425 13.7832 9.89014C13.8467 9.73779 13.999 9.66162 14.2402 9.66162H15.2051C15.112 9.02686 14.9152 8.43864 14.6147 7.89697C14.3185 7.35107 13.9377 6.87288 13.4722 6.4624C13.0109 6.05192 12.484 5.73242 11.8916 5.50391C11.2992 5.27116 10.6686 5.15479 10 5.15479C9.24251 5.15479 8.53158 5.30713 7.86719 5.61182C7.20703 5.91227 6.63997 6.31852 6.16602 6.83057C6.01367 6.97868 5.85075 7.05485 5.67725 7.05908C5.50798 7.06331 5.35986 7.00618 5.23291 6.8877C5.10173 6.77344 5.03402 6.62744 5.02979 6.44971C5.02979 6.27197 5.10807 6.10693 5.26465 5.95459Z" />
    </svg>
  );
};

const CenterIcon = ({ className }: { className?: string }) => {
  return (
    <svg
      stroke="currentColor"
      fill="none"
      strokeWidth="2"
      viewBox="0 0 24 24"
      strokeLinecap="round"
      strokeLinejoin="round"
      height="20"
      width="20"
      style={{ padding: '1px' }}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 12m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"></path>
      <path d="M4 8v-2a2 2 0 0 1 2 -2h2"></path>
      <path d="M4 16v2a2 2 0 0 0 2 2h2"></path>
      <path d="M16 4h2a2 2 0 0 1 2 2v2"></path>
      <path d="M16 20h2a2 2 0 0 0 2 -2v-2"></path>
    </svg>
  );
};

const LockIcon = () => {
  return (
    <svg
      fill="currentColor"
      height="20"
      viewBox="0 0 20 20"
      width="20"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="m15 7.79167h-2v-1.55556c0-.82512-.3161-1.61644-.8787-2.19989-.5626-.58344-1.3257-.91122-2.1213-.91122-.79565 0-1.55871.32778-2.12132.91122-.56261.58345-.87868 1.37477-.87868 2.19989v1.55556h-2c-.26522 0-.51957.10926-.70711.30374-.18753.19448-.29289.45825-.29289.73329v7.2593c0 .275.10536.5388.29289.7333.18754.1944.44189.3037.70711.3037h10c.2652 0 .5196-.1093.7071-.3037.1875-.1945.2929-.4583.2929-.7333v-7.2593c0-.27504-.1054-.53881-.2929-.73329s-.4419-.30374-.7071-.30374zm-7-1.55556c0-.55008.21071-1.07763.58579-1.46659.37507-.38897.88378-.60748 1.41421-.60748.5304 0 1.0391.21851 1.4142.60748.3751.38896.5858.91651.5858 1.46659v1.55556h-4zm7 9.85189h-10v-7.2593h10zm-4.25-3.6297c0 .1539-.044.3042-.1264.4321s-.1995.2276-.3366.2865c-.137.0589-.28783.0743-.43332.0443-.14548-.03-.27912-.1041-.38401-.2129s-.17632-.2474-.20526-.3982c-.02894-.1509-.01409-.3073.04268-.4494s.1529-.2636.27623-.3491c.12334-.0854.26834-.131.41668-.131.1989 0 .3897.0819.5303.2278.1407.1458.2197.3437.2197.5499z" />
    </svg>
  );
};

const LockOpenIcon = () => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M15 8H8V6.5C8 5.96957 8.21071 5.46086 8.58579 5.08579C8.96086 4.71071 9.46957 4.5 10 4.5C10.9606 4.5 11.825 5.1875 12.01 6.09938C12.0375 6.22823 12.1147 6.34104 12.2249 6.41328C12.335 6.48551 12.4693 6.51135 12.5984 6.48516C12.7275 6.45898 12.8411 6.38289 12.9144 6.27343C12.9877 6.16397 13.0149 6.03 12.99 5.90062C12.7075 4.50937 11.45 3.5 10 3.5C9.2046 3.50083 8.44202 3.81716 7.87959 4.37959C7.31716 4.94202 7.00083 5.7046 7 6.5V8H5C4.73478 8 4.48043 8.10536 4.29289 8.29289C4.10536 8.48043 4 8.73478 4 9V16C4 16.2652 4.10536 16.5196 4.29289 16.7071C4.48043 16.8946 4.73478 17 5 17H15C15.2652 17 15.5196 16.8946 15.7071 16.7071C15.8946 16.5196 16 16.2652 16 16V9C16 8.73478 15.8946 8.48043 15.7071 8.29289C15.5196 8.10536 15.2652 8 15 8ZM15 16H5V9H15V16ZM10.75 12.5C10.75 12.6483 10.706 12.7933 10.6236 12.9167C10.5412 13.04 10.4241 13.1361 10.287 13.1929C10.15 13.2497 9.99917 13.2645 9.85368 13.2356C9.7082 13.2066 9.57456 13.1352 9.46967 13.0303C9.36478 12.9254 9.29335 12.7918 9.26441 12.6463C9.23547 12.5008 9.25032 12.35 9.30709 12.213C9.36386 12.0759 9.45999 11.9588 9.58332 11.8764C9.70666 11.794 9.85166 11.75 10 11.75C10.1989 11.75 10.3897 11.829 10.5303 11.9697C10.671 12.1103 10.75 12.3011 10.75 12.5Z" />
    </svg>
  );
};
